/**
 * This module contains all custom logic to suggest a prompt for the set of AI features
 * the user has enabled.
 *
 * To add a suggestion that the runner already supports, simply update this module
 * to return the new suggestion. See implementation and use of {@link getModelHubSuggestions} as an example.
 *
 * To add a a new kind of suggestion, you will also need to:
 * 1. Update the type in PromptSuggestionTypes.
 * 2. Implement an execution of the prompt in runPromptSuggestion.tsx.
 * 3. Update PromptSuggestionCards to render the new type of suggestion.
 *
 * NOTE:
 * - For NEW_CHAT, benchling-objects input is only used for rendering. The object's name should be interpolated
 *   into the prompt.
 */
import {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Organization} from 'orgs/OrganizationConstants';
import EntityStore from 'stores/EntityStore';

import {AgentMode} from '../sidepanel/ChatTypeUtil';
import {PromptSuggestion, PromptSuggestionCategory} from './PromptSuggestionTypes';
import {SharedSearchResults, useSharedSearchResults} from './PromptSuggestionUtils';
/**
 * @param sharedSearchResults - Shared search results - null if not yet loaded.
 * @returns prompt suggestions - null in the array indicates a loading card.
 * @package exported for testing
 */
export function getModelHubSuggestions(
  sharedSearchResults: SharedSearchResults | null
): Array<PromptSuggestion | null> {
  if (!BenchlingConfig.MOLBIO_ENABLE_ALPHAFOLD_GA) {
    return [];
  }
  if (sharedSearchResults == null) {
    return [null]; // Loading
  }
  const protein = sharedSearchResults.recentProtein;
  if (protein == null) {
    return [];
  }
  return [
    {
      id: 'model-hub-open-3d-structure-for-recent-protein',
      summary: 'Run structure prediction',
      action: {
        input: {type: 'benchling-objects', ids: [protein.id]},
        type: 'MODEL_HUB_OPEN_3D_STRUCTURE',
      },
    },
  ];
}

/**
 * @param sharedSearchResults - Shared search results - null if not yet loaded (unused, but included for API consistency).
 * @returns prompt suggestions.
 * @package exported for testing
 */
export function getDataCaptureSuggestions(
  sharedSearchResults: SharedSearchResults | null
): Array<PromptSuggestion> {
  if (!BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT) {
    return [];
  }
  const suggestions: Array<PromptSuggestion> = [
    {
      id: 'data-capture-convert-example-protocol-to-entry',
      summary: 'Convert this protocol into a Benchling entry',
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DATA_CAPTURE,
        input: {
          type: 'demo-file',
          filename: 'elisa-protocol-email.png',
          name: 'ELISA Protocol (Example).png',
        },
        prompt:
          'Create an entry from the protocol in the attached email. List the materials in a table format and include clear numbered steps.',
      },
    },
  ];

  const recentEntry = sharedSearchResults?.recentEntries[0] ?? null;
  if (recentEntry != null) {
    suggestions.push({
      id: 'data-capture-convert-entry-to-template',
      summary: 'Create a template from this entry',
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DATA_CAPTURE,
        input: {type: 'benchling-objects', ids: [recentEntry.id]},
        prompt: `Convert this entry into a template: ${recentEntry.name}`,
      },
    });
  }
  return suggestions;
}

/**
 * @param sharedSearchResults - Shared search results - null if not yet loaded.
 * @returns prompt suggestions (static suggestions returned immediately, search-dependent added when loaded).
 * @package exported for testing
 */
export function getAskSuggestions(sharedSearchResults: SharedSearchResults | null): Array<PromptSuggestion> {
  if (!BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH) {
    return [];
  }

  const suggestions: Array<PromptSuggestion> = [
    {
      id: 'ask-explain-experimental-technique',
      summary: 'How do I create new DNA sequences in Benchling?',
      action: {
        input: {type: 'none'},
        type: 'NEW_CHAT',
        prompt: 'How do I create new DNA sequences in Benchling?',
        agentMode: AgentMode.ASK,
      },
    },
  ];

  const folderItem = sharedSearchResults?.recentFolderItem ?? null;
  if (folderItem != null) {
    suggestions.push({
      id: 'ask-summarize-entity-lineage',
      summary: 'Summarize the lineage of this entity',
      action: {
        input: {type: 'benchling-objects', ids: [folderItem.id]},
        type: 'NEW_CHAT',
        prompt: `Summarize the details and lineage of this entity: ${folderItem.name}`,
        agentMode: AgentMode.ASK,
      },
    });
  }

  const recentEntry = sharedSearchResults?.recentEntries?.[0] ?? null;
  if (recentEntry != null) {
    suggestions.push({
      id: 'ask-write-summary-of-recent-entry',
      summary: 'Write a summary of my recent entry',
      action: {
        input: {type: 'benchling-objects', ids: [recentEntry.id]},
        type: 'NEW_CHAT',
        prompt: `Write a summary of my recent entry: ${recentEntry.name}`,
        agentMode: AgentMode.ASK,
      },
    });
  }

  return suggestions;
}

/**
 * @param sharedSearchResults - Shared search results - null if not yet loaded.
 * @returns prompt suggestions (static suggestions returned immediately, search-dependent added when loaded).
 * @package exported for testing
 */
export function getDeepResearchSuggestions(
  sharedSearchResults: SharedSearchResults | null
): Array<PromptSuggestion> {
  if (!BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH) {
    return [];
  }

  // Static suggestions that don't require search
  const suggestions: Array<PromptSuggestion> = [
    {
      id: 'deep-research-summarize-last-month-experiments',
      summary: 'Summarize my experiments from the last month into a report',
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DEEP_RESEARCH,
        input: {type: 'none'},
        // This slightly longer prompt is more reliably getting the dates right
        prompt: 'Summarize my experiments going back from today over the last month into a report',
      },
    },
  ];

  const recentStudy = sharedSearchResults?.recentStudy ?? null;
  if (recentStudy != null) {
    suggestions.push({
      id: 'deep-research-summarize-study-data',
      summary: `Summarize the following study into an executive summary`,
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DEEP_RESEARCH,
        input: {type: 'benchling-objects', ids: [recentStudy.id]},
        prompt: `Summarize the following study into an executive summary: ${recentStudy.name}`,
      },
    });
  }

  const recentEntries = sharedSearchResults?.recentEntries ?? null;
  if (recentEntries != null && recentEntries.length > 0) {
    suggestions.push(
      {
        id: 'deep-research-summarize-notebook-entries',
        summary: `Generate an executive summary for these notebook entries`,
        action: {
          type: 'NEW_CHAT',
          agentMode: AgentMode.DEEP_RESEARCH,
          input: {type: 'benchling-objects', ids: recentEntries.map((entry) => entry.id)},
          prompt: `Summarize the following notebook entries for ${recentEntries.map((entry) => entry.name).join(', ')} into an executive summary. Include completion and review status, research goals met, any deviations from protocol, and remaining work. Format as an executive summary showing completed vs. future experiments.`,
        },
      },
      {
        id: 'deep-research-analyze-notebook-entries-for-data-discrepancies',
        summary: `Analyze the following notebook entries for data discrepancies`,
        action: {
          type: 'NEW_CHAT',
          agentMode: AgentMode.DEEP_RESEARCH,
          input: {type: 'benchling-objects', ids: recentEntries.map((entry) => entry.id)},
          prompt: `Analyze the Notebook entries and results data for ${recentEntries.map((entry) => entry.name).join(', ')} including links to each and noting any atipical data logging practices such as data logged in entries not created from templates, or any data inconsistencies between entries for additional review.`,
        },
      }
    );
  }

  const project = sharedSearchResults?.recentProject ?? null;
  if (project != null) {
    suggestions.push({
      id: 'deep-research-summarize-project-data',
      summary: `Find research gaps in Project ${project.name}`,
      action: {
        type: 'NEW_CHAT',
        agentMode: AgentMode.DEEP_RESEARCH,
        input: {type: 'none'},
        prompt: `Analyze Project ${project.name} for research gaps. Focus on ongoing research and summarize the current state of the project before suggesting any new research directions.`,
      },
    });
  }

  const user = EntityStore.currentUser();
  const isOrgAdmin = user?.organizations?.some((org: Organization) => org.admin);
  if (isOrgAdmin || user?.isTenantAdmin) {
    suggestions.push(
      {
        id: 'deep-research-audit-notebook-entries-and-entities',
        summary: 'Audit all entries/entities created last quarter for compliance issues',
        action: {
          type: 'NEW_CHAT',
          agentMode: AgentMode.DEEP_RESEARCH,
          input: {type: 'none'},
          prompt:
            'Audit all notebook entries and entities created in the last quarter for compliance issues. Identify: 1) Entries without project associations, 2) Entities missing required metadata fields (lot number, expiration date, creator), 3) Users with edit permissions on archived projects, 4) Sensitive data (patient/clinical) accessible to non-clinical team members. Output a prioritized action list with specific remediation steps.',
        },
      },
      {
        id: 'deep-research-identify-common-work-types-in-notebook-entries',
        summary: 'Identify patterns in notebook entries not created from a template',
        action: {
          type: 'NEW_CHAT',
          agentMode: AgentMode.DEEP_RESEARCH,
          input: {type: 'none'},
          prompt:
            'What is the most common type of work carried out in notebook entries not created from a template? Focus on only Notebook entries created in the last 12 months. For whatever patterns you can identify, suggest which could have been accomplished with a template but were not and which have no logical template to use for the work. Of those with no logical existing template, what are the top 3 for which it might make the most sense to create a new template to support that type of work?',
        },
      }
    );
  }

  return suggestions;
}

const CANNED_SUGGESTIONS_BY_SECTION: Record<PromptSuggestionCategory, Array<PromptSuggestion>> = {
  [PromptSuggestionCategory.FIND_DATA]: [
    {
      id: 'canned-antibody-lot-lineage',
      summary: 'Show me the lineage of antibody lot A143-2',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-find-sequences-cmv-promoter',
      summary: 'Find all sequences with a CMV promoter',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },

    {
      id: 'canned-historical-conditions-hela-cell-line',
      summary: 'Show all historical culture conditions for this HeLa cell line',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.ASK, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.WRITE_REPORTS]: [
    {
      id: 'canned-report-key-outcomes-from-study',
      summary: 'Generate a nonclinical study report for Study-S-Alpha-143',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-report-progress-program',
      summary: 'Write a progress report on Program Helix to share with my director',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-report-executive-summary',
      summary: 'Make a CoA for Protein Lot PROT-023',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DEEP_RESEARCH, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.DESIGN_EXPERIMENTS]: [
    {
      id: 'canned-design-elisa-protocol',
      summary: 'Draft an ELISA protocol using the best performing parameters found in scientific literature',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-design-qpcr-experiment',
      summary:
        'Design a qPCR experiment for my target gene across 3 conditions, based on similar ones my lab has run',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-design-from-photo-meeting-notes',
      summary: 'Convert this photo of my meeting notes with my PI into an organized experiment entry',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.CAPTURE_DATA]: [
    {
      id: 'canned-import-cro-report',
      summary:
        'Import this CRO report into my entry, registering samples and structuring clinical chemistry results',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-extract-nanodrop-photo',
      summary: 'Extract the concentration and A260/280 values from this photo of my nanodrop display',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-process-coas',
      summary: 'Process my CoAs and import the reagent details into my entry',
      action: {type: 'NEW_CHAT', agentMode: AgentMode.DATA_CAPTURE, prompt: '', input: {type: 'none'}},
    },
  ],
  [PromptSuggestionCategory.PREDICT_STRUCTURES]: [
    {
      id: 'canned-predict-protein-structure',
      summary: 'Predict the 3D structure of my novel protein P-481',
      action: {type: 'NEW_CHAT_PLACEHOLDER', agentMode: 'PREDICT', prompt: '', input: {type: 'none'}},
    },
    {
      id: 'canned-model-complex-between-proteins',
      summary: 'Model the complex between proteins A-243 and A-546',
      action: {type: 'NEW_CHAT_PLACEHOLDER', agentMode: 'PREDICT', prompt: '', input: {type: 'none'}},
    },
  ],
};

export function getCannedPromptSuggestions(category: PromptSuggestionCategory): Array<PromptSuggestion> {
  return CANNED_SUGGESTIONS_BY_SECTION[category];
}

/**
 * Returns grouped prompt suggestions where each group represents a different use case.
 * Each group can contain multiple suggestions that will be cycled through in the UI.
 *
 * This hook fires all search queries once at the beginning in parallel. Static prompts
 * render immediately, while search-dependent prompts are added when searches complete.
 */
export function useGetGroupedPromptSuggestions(): Array<Array<PromptSuggestion | null>> {
  const sharedSearchResults = useSharedSearchResults();

  const askSuggestions = useMemo(() => getAskSuggestions(sharedSearchResults), [sharedSearchResults]);
  const deepResearchSuggestions = useMemo(
    () => getDeepResearchSuggestions(sharedSearchResults),
    [sharedSearchResults]
  );
  const dataCaptureSuggestions = useMemo(
    () => getDataCaptureSuggestions(sharedSearchResults),
    [sharedSearchResults]
  );
  const modelHubSuggestions = useMemo(
    () => getModelHubSuggestions(sharedSearchResults),
    [sharedSearchResults]
  );

  return useMemo(
    () =>
      [askSuggestions, deepResearchSuggestions, dataCaptureSuggestions, modelHubSuggestions].filter(
        (group) => group.length > 0
      ),
    [askSuggestions, deepResearchSuggestions, dataCaptureSuggestions, modelHubSuggestions]
  );
}
